import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { personalInfo } from "@/mocks/devData";

gsap.registerPlugin(ScrollTrigger);

const stats = [
  {
    num: 18,
    suffix: "+",
    label: "Projects Built",
    icon: "ri-code-s-slash-line",
  },
  { num: 2, suffix: "+", label: "Years Coding", icon: "ri-calendar-line" },
  { num: 20, suffix: "+", label: "Technologies", icon: "ri-stack-line" },
  { num: 100, suffix: "%", label: "Dedication", icon: "ri-heart-line" },
];

function useCountUp(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!start) return;
    startTimeRef.current = null;
    const easeOutExpo = (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
    const animate = (ts: number) => {
      if (!startTimeRef.current) startTimeRef.current = ts;
      const elapsed = ts - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      setCount(Math.round(easeOutExpo(progress) * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [start, target, duration]);

  return count;
}

function StatCard({
  num,
  suffix,
  label,
  icon,
  isLast,
}: (typeof stats)[0] & { isLast?: boolean }) {
  const [started, setStarted] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const count = useCountUp(num, 1600 + num * 6, started);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className="stat-card p-5 text-center hover:-translate-y-1 transition-all duration-300 group cursor-default"
      style={
        !isLast
          ? { borderRight: "1px solid rgba(255,255,255,0.07)" }
          : undefined
      }
    >
      <div
        className="w-8 h-8 flex items-center justify-center mx-auto mb-2 rounded-lg"
        style={{
          background: "rgba(34,197,94,0.1)",
          transform: "translateZ(8px)",
        }}
      >
        <i className={`${icon} text-green-400 text-base`} />
      </div>
      <div
        className="text-2xl font-black text-green-400"
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          transform: "translateZ(6px)",
        }}
      >
        {count}
        {suffix}
      </div>
      <div className="text-gray-400 text-xs mt-1 font-mono">{label}</div>
    </div>
  );
}

const highlights = [
  { icon: "ri-reactjs-line", label: "Full Stack Developer" },
  { icon: "ri-server-line", label: "MERN Stack Developer" },
  { icon: "ri-layout-line", label: "Front End Developer" },
  { icon: "ri-global-line", label: "Web Developer" },
];

export default function DevAbout() {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const bioRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLDivElement>(null);
  const linksRef = useRef<HTMLDivElement>(null);
  const floatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 70, rotateX: -25, transformPerspective: 1000 },
        {
          opacity: 1,
          y: 0,
          rotateX: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 85%",
            once: true,
          },
        },
      );
      if (tagsRef.current) {
        const tags = tagsRef.current.querySelectorAll(".tag-item");
        gsap.fromTo(
          tags,
          { opacity: 0, scale: 0.6, y: 30 },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 0.55,
            stagger: 0.09,
            ease: "back.out(2.5)",
            scrollTrigger: {
              trigger: tagsRef.current,
              start: "top 88%",
              once: true,
            },
          },
        );
      }
      gsap.fromTo(
        bioRef.current,
        { opacity: 0, x: -60, rotateY: 15, transformPerspective: 1000 },
        {
          opacity: 1,
          x: 0,
          rotateY: 0,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: {
            trigger: bioRef.current,
            start: "top 88%",
            once: true,
          },
        },
      );
      gsap.fromTo(
        imageRef.current,
        { opacity: 0, x: 60, rotateY: -15, transformPerspective: 1000 },
        {
          opacity: 1,
          x: 0,
          rotateY: 0,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: {
            trigger: imageRef.current,
            start: "top 88%",
            once: true,
          },
        },
      );
      if (statsRef.current) {
        const cards = statsRef.current.querySelectorAll(".stat-card");
        gsap.fromTo(
          cards,
          { opacity: 0, y: 50, rotateY: -30, transformPerspective: 900 },
          {
            opacity: 1,
            y: 0,
            rotateY: 0,
            duration: 0.75,
            stagger: 0.13,
            ease: "back.out(1.6)",
            scrollTrigger: {
              trigger: statsRef.current,
              start: "top 85%",
              once: true,
            },
          },
        );
      }
      gsap.fromTo(
        linksRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power2.out",
          scrollTrigger: {
            trigger: linksRef.current,
            start: "top 90%",
            once: true,
          },
        },
      );
      if (floatRef.current) {
        gsap.to(floatRef.current, {
          y: -8,
          duration: 2.2,
          ease: "sine.inOut",
          yoyo: true,
          repeat: -1,
        });
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-20 md:py-28 overflow-hidden"
      style={{
        background: `
          radial-gradient(at 20% 20%, rgba(22,163,74,0.05) 0px, transparent 50%),
          radial-gradient(at 80% 80%, rgba(22,163,74,0.04) 0px, transparent 50%),
          #000000
        `,
        perspective: "1200px",
      }}
    >
      <div
        className="orb-1 absolute top-0 right-0 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(22,163,74,0.05) 0%, transparent 65%)",
          filter: "blur(60px)",
        }}
      />
      <div
        className="orb-2 absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(22,163,74,0.04) 0%, transparent 65%)",
          filter: "blur(60px)",
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
        <div
          ref={headerRef}
          className="mb-14"
          style={{ transformStyle: "preserve-3d" }}
        >
          <p className="text-xs font-mono text-gray-400 tracking-widest uppercase mb-3">
            Get To Know Me
          </p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2
              className="text-4xl md:text-5xl font-light text-white tracking-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              About <span className="font-black text-green-600">Me</span>
            </h2>
            <p className="text-gray-400 text-sm font-mono">
              {personalInfo.location}
            </p>
          </div>
          <div
            className="mt-6 h-px"
            style={{ background: "rgba(255,255,255,0.06)" }}
          />
        </div>

        {/* Bio + Image */}
        <div className="grid lg:grid-cols-5 gap-10 mb-12 items-start">
          {/* Left: text content */}
          <div
            ref={bioRef}
            className="lg:col-span-3"
            style={{ transformStyle: "preserve-3d" }}
          >
            {/* Tags */}
            <div ref={tagsRef} className="flex flex-wrap gap-3 mb-8">
              {highlights.map((h) => (
                <div
                  key={h.label}
                  className="tag-item flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium text-gray-200 hover:text-green-400 hover:scale-105 transition-all duration-200 cursor-default"
                  style={{
                    border: "1px solid rgba(255,255,255,0.1)",
                    background: "rgba(255,255,255,0.04)",
                  }}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${h.icon} text-green-600 text-sm`} />
                  </div>
                  {h.label}
                </div>
              ))}
            </div>

            <h3
              className="text-xl md:text-2xl font-semibold text-white mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              A Dedicated{" "}
              <span className="text-green-600">Full Stack Developer</span>{" "}
              Building Modern Web Solutions
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              {personalInfo.bio}
            </p>
            <p className="text-gray-300 text-sm leading-relaxed mb-7">
              {personalInfo.bio2}
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {[
                {
                  label: "Location",
                  value: personalInfo.location,
                  icon: "ri-map-pin-line",
                },
                {
                  label: "Email",
                  value: personalInfo.email,
                  icon: "ri-mail-line",
                },
                {
                  label: "Status",
                  value: "Open to opportunities",
                  icon: "ri-checkbox-circle-line",
                },
                {
                  label: "Focus",
                  value: "Remote Roles",
                  icon: "ri-flight-takeoff-line",
                },
              ].map((f) => (
                <div
                  key={f.label}
                  className="flex items-center gap-3 rounded-xl px-2 py-3"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: "rgba(34,197,94,0.1)" }}
                  >
                    <i className={`${f.icon} text-green-600 text-sm`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-gray-500 text-[10px] font-mono uppercase tracking-wide">
                      {f.label}
                    </div>
                    <div
                      className={`text-gray-100 text-sm ${
                        f.label === "Email"
                          ? "break-all leading-snug"
                          : "truncate"
                      }`}
                      title={f.value}
                    >
                      {f.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Links */}
            <div ref={linksRef} className="flex flex-wrap gap-3">
              <a
                href="#contact"
                className="btn-3d flex items-center gap-2 px-5 py-2.5 text-xs sm:text-sm"
                style={{
                  background:
                    "linear-gradient(180deg, #22c55e 0%, #16a34a 100%)",
                  color: "#000",
                  fontWeight: 700,
                  borderRadius: 8,
                  boxShadow:
                    "0 4px 0 #14532d, inset 0 1px 0 rgba(255,255,255,0.15)",
                }}
              >
                <i className="ri-send-plane-line" /> Let&apos;s Work Together
              </a>
              <a
                href={personalInfo.github}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-3d btn-3d-outline flex items-center gap-2 px-4 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm"
              >
                <i className="ri-github-fill" /> GitHub
              </a>
              <a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-3d btn-3d-ghost flex items-center gap-2 px-4 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm"
              >
                <i className="ri-linkedin-box-fill" /> LinkedIn
              </a>
            </div>
          </div>

          {/* Right: image */}
          <div
            ref={imageRef}
            className="lg:col-span-2 relative flex justify-center lg:justify-end"
            style={{ transformStyle: "preserve-3d" }}
          >
            <div className="relative w-full max-w-[320px]">
              {/* Glow behind image */}
              <div
                className="absolute -inset-4 rounded-[2rem] pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(34,197,94,0.18) 0%, transparent 70%)",
                  filter: "blur(30px)",
                }}
              />

              <div
                className="relative rounded-3xl overflow-hidden"
                style={{
                  border: "1px solid rgba(34,197,94,0.25)",
                  background: "#0a0a0a",
                }}
              >
                <div
                  className="h-[2px] w-full"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(34,197,94,0.7), transparent)",
                  }}
                />
                <img
                  src="https://i.postimg.cc/x8Lg7QCM/safin.webp"
                  alt="Hasibur Rahman Safin"
                  loading="lazy"
                  className="w-full aspect-[4/5] object-cover"
                  style={{ filter: "brightness(0.96) contrast(1.05)" }}
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 35%)",
                  }}
                />
              </div>

              {/* Floating status badge */}
              <div
                ref={floatRef}
                className="absolute -bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap"
                style={{
                  background: "#0e0e0e",
                  border: "1px solid rgba(34,197,94,0.3)",
                }}
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
                </span>
                <span className="text-green-300 text-xs font-mono">
                  Available for hire
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div
          ref={statsRef}
          className="grid grid-cols-2 md:grid-cols-4 rounded-3xl overflow-hidden mb-4"
          style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.08)",
            perspective: "800px",
          }}
        >
          {stats.map((s, i) => (
            <StatCard key={s.label} {...s} isLast={i === stats.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
